import { useState, useRef, createRef } from 'react';
import PropTypes from 'prop-types';

import { formatBytes, getClassNames } from '../../../../utils/util';
import { documentCategoryStatusList, documentStatusList } from '../../../../utils/contract-util';
import { ADMIN, BUYER, PENDING, REJECTED } from '../../../../utils/constants';

import FilePicker from '../../inputs/file-picker/FilerPicker';
import Button from '../../button/Button';
import Dropdown from '../../inputs/dropdown/Dropdown';
import Modal from '../../modal/Modal';
import TextInput from '../../inputs/text-input/TextInput';
import Tooltip from '../../tooltip/ToolTip';

import { ReactComponent as TrashIcon } from '../../../../resources/icons/trash.svg';

import './contract-documents.scss';

const ContractDocuments = ({
  folder,
  addDocument,
  role,
  canUserEdit,
  updateCategoryStatus,
  updateDocumentStatus,
  deleteDocument,
  downloadDocument,
  reviewDocument,
  latest,
}) => {
  const [open, setOpen] = useState(false);
  const [startCloseAnimation, setStartCloseAnimation] = useState(true);
  const [rejectedDocument, setRejectedDocument] = useState(undefined);
  const [rejectedDocumentCategory, setRejectedDocumentCategory] = useState(undefined);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [displayTooltipName, setDisplayTooltipName] = useState('');

  const refs = useRef([]);

  const getNumberOfDocuments = () => {
    if (!folder) {
      return '';
    }
    const categories = folder.categories;
    let noOfDocs = 0;

    categories.forEach((category) => {
      noOfDocs = noOfDocs + category.documents.length;
    });

    let noOfDocuments = '';

    if (noOfDocs !== 0) {
      noOfDocuments = noOfDocuments.concat(noOfDocs.toString());

      if (noOfDocs === 1) {
        noOfDocuments = noOfDocuments.concat(' Document');
      } else {
        noOfDocuments = noOfDocuments.concat(' Documents');
      }
    }

    return noOfDocuments;
  };

  const getToolTipText = (categoryName) => {
    switch (categoryName) {
      case 'Notes':
        return 'Any additional comments you wish to make.';
      case 'Sales & Purchase Agreement':
        return 'An agreement between a buyer and a seller of company stock, or other assets.';
      case 'Term Sheets':
        return 'A nonbinding agreement outlining the basic terms and conditions under which an investment will be made.';
      case 'Insurance Cover':
        return 'Sets the limit of the financial cover one can avail.';
      case 'Photos':
        return 'Add any photos that you might have of the product/s.';
      case 'Specifications':
        return 'Specify the specifications of the product/s.';
      case 'Laboratory Reports':
        return 'Add any lab reports required for your product/s.';
      case 'Certificate of Origin':
        return 'Provide the Certification of Origin for your product/s.';
      case 'Export Documents':
        return 'A document that states that permission has been given to export something legally.';
      case 'Quotes':
        return 'A quotation giving the estimated cost for a particular job or service.';
      case 'Invoices':
        return 'A bill to be paid for the products you wish to buy/sell.';
      case 'Insurance Quotes':
        return 'The estimated cost of a policy based on the information you provide to the insurer and the coverage you select.';
      case 'Delivery Notes':
        return 'Notes specific to where and how you want the product/s to be delivered.';
      case 'Release Notes':
        return 'Notes specific to where and how you want the product/s to be released.';
      case 'Proofs of Payment':
        return 'A proof of payment.';
      case 'Pro-Forma Invoices':
        // eslint-disable-next-line max-len
        return 'Is a preliminary bill or estimated invoice which is used to request payment from the committed buyer for goods or services before they are supplied.';
      case 'Customs Regulations':
        // eslint-disable-next-line max-len
        return 'Legal and other provisions governing the import, export and transit of goods and means of payment, and provisions concerning taxes and customs duties, and customs prohibitions, restrictions, and controls.';
      case 'Export Regulations':
        // eslint-disable-next-line max-len
        return 'Any national and international laws and regulations including, without limitation, laws and regulations applicable to import, re-export or otherwise transferred technical data or technology to which the product or any part of the product may be subject.';
      case 'VAT Duties / Tariffs':
        // eslint-disable-next-line max-len
        return 'Is an indirect tax that is largely directed at the domestic consumption of goods and services and at goods imported into a country.';
      case 'Import Duties':
        return 'Is a tax collected on imports and some exports by a country`s customs authorities';
      case 'Pictures':
        return 'Add any pictures of any incident you may have had while delivering the product/s.';
      case 'Damages / Product Issues':
        return 'Specify what damage/s to the product.';
      case 'Rejections':
        return 'Specify why you have rejected the product.';
      case 'Payments Not Reflecting':
        return 'Provide proof that the payment has not reflected in the bank account.';
      case 'Lost Cargo':
        return 'Provide proof that the cargo/products have been lost/stolen.';
      default:
        return '';
    }
  };

  const handleOverviewClick = () => {
    let timeout;

    if (open) {
      setStartCloseAnimation(true);
      timeout = setTimeout(() => setOpen(false), 300);
    } else {
      setOpen(true);
      setStartCloseAnimation(false);
    }

    return () => clearTimeout(timeout);
  };

  const handleDocumentStatusChange = (option, category, document, facilitator) => {
    if (option.value === REJECTED && role === ADMIN) {
      setRejectedDocument(document);
      setRejectedDocumentCategory(category);
      setShowRejectionModal(true);
    } else if (role === ADMIN) {
      reviewDocument(document.externalReference, option.value);
    }

    updateDocumentStatus(folder.name, category.name, document.externalReference, option.value, facilitator);
  };

  const hideRejectionModal = () => {
    setRejectionMessage('');
    setRejectedDocumentCategory(undefined);
    setErrors((errors) => ({ ...errors, rejectionMessage: false }));
    setShowRejectionModal(false);
  };

  const handleCancelClick = () => {
    updateDocumentStatus(folder.name, rejectedDocumentCategory.name, rejectedDocument.externalReference, PENDING, true);
    hideRejectionModal();
  };

  const handleSendClick = () => {
    if (!rejectionMessage) {
      setErrors((errors) => ({ ...errors, rejectionMessage: true }));
      return;
    }

    reviewDocument(rejectedDocument.externalReference, REJECTED, rejectionMessage, hideRejectionModal);
  };

  const renderDocuments = (category) => {
    return category.documents?.map((document, index) => {
      return (
        <div className="contract-documents__details__document" key={index}>
          <div className="contract-documents__details__document__info">
            <p
              className={getClassNames('contract-documents__details__document__name', {
                clickable: document.externalReference,
              })}
              onClick={() => downloadDocument(document.externalReference, document.name)}
            >
              {document.name}
            </p>
            <p className="contract-documents__details__document__size">{formatBytes(document.size)}</p>
            {!document.externalReference && (
              <TrashIcon
                className="contract-documents__details__document__info__icon"
                onClick={() => deleteDocument(folder.name, category.name, index)}
              />
            )}
          </div>
          {document.externalReference && document.buyerStatus && document.atpStatus && (
            <>
              <div className="contract-documents__details__document__status">
                <p>Buyer:</p>
                {role === BUYER && canUserEdit ? (
                  <Dropdown
                    name="buyer-status"
                    value={{ label: document.buyerStatus, value: document.buyerStatus }}
                    options={documentStatusList}
                    onChange={(option) => handleDocumentStatusChange(option, category, document, false)}
                  />
                ) : (
                  <p>{document.buyerStatus}</p>
                )}
              </div>
              <div className="contract-documents__details__document__status">
                <p>ATP Facilitator:</p>
                {role === ADMIN && latest ? (
                  <Dropdown
                    name="facilitator-status"
                    value={{ label: document.atpStatus, value: document.atpStatus }}
                    options={documentStatusList}
                    onChange={(option) => handleDocumentStatusChange(option, category, document, true)}
                  />
                ) : (
                  <p>{document.atpStatus}</p>
                )}
              </div>
            </>
          )}
        </div>
      );
    });
  };

  const renderCategories = () => {
    return folder?.categories?.map((category, index) => {
      refs.current[index] = createRef();

      return (
        <div className="contract-documents__details__category" key={index}>
          <div className="contract-documents__details__category__title">
            <div
              className="contract-documents__details__category__title__text"
              onMouseEnter={() => setDisplayTooltipName(category.name)}
              onMouseLeave={() => setDisplayTooltipName('')}
            >
              <div className="contract-documents__details__category__title__text__container">
                <p>{category.name}</p>
                {displayTooltipName === category.name && (
                  <div className="contract-documents__tooltip">
                    <Tooltip text={getToolTipText(category.name)} maxWidth={350} toolTipLeft={false} />
                  </div>
                )}
              </div>
            </div>
            {role === BUYER && canUserEdit ? (
              <Dropdown
                name="status"
                value={{ label: category.status, value: category.status }}
                options={documentCategoryStatusList}
                onChange={(option) => updateCategoryStatus(folder.name, category.name, option.value)}
              />
            ) : (
              <p>{category.status}</p>
            )}
          </div>
          {renderDocuments(category)}
          {canUserEdit && (
            <FilePicker
              fileRef={refs.current[index]}
              enableFileLimit={true}
              fileType="*"
              onChange={({ target }) => addDocument(folder.name, category.name, target.files[0])}
              triggerElement={<Button onClick={() => refs.current[index].current.click()}>Add File</Button>}
            />
          )}
        </div>
      );
    });
  };

  return (
    <>
      <section className="contract-documents">
        <section
          className={getClassNames('contract-documents__overview', { open: !startCloseAnimation })}
          onClick={handleOverviewClick}
        >
          <p className="contract-documents__overview__title">{folder?.name}</p>
          <p className="contract-documents__overview__number">{getNumberOfDocuments()}</p>
          <div className="contract-documents__overview__icon-container">
            <div className="contract-documents__overview__icon" />
            <div className={getClassNames('contract-documents__overview__icon', { open: !startCloseAnimation })} />
          </div>
        </section>
        {open && (
          <section className={getClassNames('contract-documents__details', { close: startCloseAnimation })}>
            {renderCategories()}
          </section>
        )}
      </section>

      <Modal show={showRejectionModal} setShow={setShowRejectionModal}>
        <section className="contract-documents__rejection-modal">
          <h1>Reject Document</h1>
          <p>Why is the document being rejected?</p>
          <TextInput
            name="rejection-message"
            value={rejectionMessage}
            onChange={({ target }) => setRejectionMessage(target.value)}
            error={errors.rejectionMessage}
            placeholder="Message..."
            multiline
          />

          <section className="contract-documents__rejection-modal__actions">
            <Button className="contract-documents__rejection-modal__button" onClick={handleCancelClick} secondary>
              Cancel
            </Button>

            <Button className="contract-documents__rejection-modal__button" onClick={handleSendClick}>
              Send
            </Button>
          </section>
        </section>
      </Modal>
    </>
  );
};

ContractDocuments.propTypes = {
  folder: PropTypes.shape({}).isRequired,
  addDocument: PropTypes.func.isRequired,
  role: PropTypes.string.isRequired,
  updateCategoryStatus: PropTypes.func.isRequired,
  updateDocumentStatus: PropTypes.func.isRequired,
  deleteDocument: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  reviewDocument: PropTypes.func.isRequired,
  latest: PropTypes.bool.isRequired,
};

export default ContractDocuments;
